import { useState } from "react";
import { CirclePlus } from "lucide-react";

import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Link,
  Table,
  Dialog,
  SimpleGrid,
  CloseButton,
} from "@libs/design";

import { StatusBadge } from "../../status-badge";
import { SeverityBadge } from "../../severity-badge";
import { getScrollbarStyles, formatDateTime } from "../../../utils";
import { usePostCreateTicket } from "../../../api";

export type Alert = {
  id: string;
  country: string;
  company: string;
  category: string;
  severity: string;
  delta: number;
  created: string;
  status: string;
  ticketId?: string | null;
  ticketNumber?: string | null;
  ticketSysId?: string | null;
  ticketCreatedAt?: string | null;
};

export type AlertsTableProps = {
  data: Alert[];
  showFullTable?: boolean;
  embedded?: boolean;
  onTicketCreated?: () => void;
};

const FONT_SIZE = 12;

export const AlertsTable = ({
  data,
  showFullTable = false,
  embedded = false,
  onTicketCreated,
}: AlertsTableProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAlert] = useState<Alert | null>(null);
  const [creatingTicketAlertId, setCreatingTicketAlertId] = useState<string | null>(null);
  const [ticketsByAlertId, setTicketsByAlertId] = useState<
    Record<string, { ticketNumber: string; ticketSysId: string }>
  >({});
  const { mutateAsync: createTicket } = usePostCreateTicket();
  const colors = {
    headerBg: "#F9FAFB",
    headerText: "#6B7280",
    rowHover: "#F9FAFB",
    textPrimary: "#111827",
    textSecondary: "#6B7280",
    link: "#6366F1",
    linkHover: "#4F46E5",
    border: "#E5E7EB",
    id: "#00927B",
    ticket: "#16A34A",
    ticketBg: "#dcf2e4",
    ticketHover: "#c9e9d7",
  };

  const getTicketLink = (ticketSysId: string) =>
    `https://phoenixdev.service-now.com/nav_to.do?uri=incident.do?sys_id=${ticketSysId}`;

  const content = (
    <>
      <Box p={6} borderBottomWidth="1px" style={{ borderColor: colors.border }}>
        <Flex justify="space-between" align="center">
          <Box>
            <Heading fontSize="md" fontWeight="semibold" color="gray.900" mb={1}>
              {showFullTable ? "All Alerts" : "Recent Alerts"}
            </Heading>
            <Text fontSize="xs" color="gray.500">
              {showFullTable
                ? "Complete alert log with status tracking"
                : "Latest data quality alerts"}
            </Text>
          </Box>
          {!showFullTable && (
            <Button
              variant="ghost"
              fontWeight="medium"
              borderWidth={0}
              style={{ fontSize: FONT_SIZE, color: colors.id }}
              _hover={{
                color: colors.linkHover,
                backgroundColor: "#F3F4F6",
              }}
              _focus={{ boxShadow: "none", outline: "none", borderWidth: 0 }}
              _focusVisible={{
                boxShadow: "none",
                outline: "none",
                borderWidth: 0,
              }}
              _active={{ borderWidth: 0 }}
              onClick={() => setIsModalOpen(true)}
            >
              View All →
            </Button>
          )}
        </Flex>
      </Box>
      <Box px={6} py={4}>
        <Box
          borderRadius="md"
          borderWidth="1px"
          overflowX="auto"
          overflowY="auto"
          maxH={showFullTable ? "60vh" : "460px"}
          style={{ borderColor: colors.border }}
          css={{
            "&::-webkit-scrollbar": { width: 2 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "#D1D5DB",
              borderRadius: 1,
            },
          }}
        >
          <Table.Root
            css={{
              "& th, & td": { padding: "14px 24px" },
              "& th": { borderBottom: `1px solid ${colors.border}` },
            }}
          >
            <Table.Header
              position="sticky"
              top={0}
              zIndex={1}
              style={{ backgroundColor: colors.headerBg }}
            >
              <Table.Row style={{ backgroundColor: colors.headerBg }}>
                <Table.ColumnHeader
                  textTransform="uppercase"
                  letterSpacing="wider"
                  px={6}
                  py={3.5}
                  style={{
                    color: colors.headerText,
                    fontSize: FONT_SIZE,
                    fontWeight: 500,
                  }}
                >
                  Alert ID
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  textTransform="uppercase"
                  letterSpacing="wider"
                  px={6}
                  py={3.5}
                  style={{
                    color: colors.headerText,
                    fontSize: FONT_SIZE,
                    fontWeight: 500,
                  }}
                >
                  Country
                </Table.ColumnHeader>
                {showFullTable && (
                  <Table.ColumnHeader
                    textTransform="uppercase"
                    letterSpacing="wider"
                    px={6}
                    py={3.5}
                    style={{
                      color: colors.headerText,
                      fontSize: FONT_SIZE,
                      fontWeight: 500,
                    }}
                  >
                    Company
                  </Table.ColumnHeader>
                )}
                <Table.ColumnHeader
                  textTransform="uppercase"
                  letterSpacing="wider"
                  px={6}
                  py={3.5}
                  style={{
                    color: colors.headerText,
                    fontSize: FONT_SIZE,
                    fontWeight: 500,
                  }}
                >
                  Category
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  textTransform="uppercase"
                  letterSpacing="wider"
                  px={6}
                  py={3.5}
                  style={{
                    color: colors.headerText,
                    fontSize: FONT_SIZE,
                    fontWeight: 500,
                  }}
                >
                  Severity
                </Table.ColumnHeader>
                {showFullTable && (
                  <>
                    <Table.ColumnHeader
                      textTransform="uppercase"
                      letterSpacing="wider"
                      px={6}
                      py={3.5}
                      style={{
                        color: colors.headerText,
                        fontSize: FONT_SIZE,
                        fontWeight: 500,
                      }}
                    >
                      Delta
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      textTransform="uppercase"
                      letterSpacing="wider"
                      px={6}
                      py={3.5}
                      style={{
                        color: colors.headerText,
                        fontSize: FONT_SIZE,
                        fontWeight: 500,
                      }}
                    >
                      Created
                    </Table.ColumnHeader>
                  </>
                )}
                <Table.ColumnHeader
                  textTransform="uppercase"
                  letterSpacing="wider"
                  px={6}
                  py={3.5}
                  style={{
                    color: colors.headerText,
                    fontSize: FONT_SIZE,
                    fontWeight: 500,
                  }}
                >
                  Status
                </Table.ColumnHeader>
                {showFullTable && (
                  <Table.ColumnHeader
                    textTransform="uppercase"
                    letterSpacing="wider"
                    px={6}
                    py={3.5}
                    style={{
                      color: colors.headerText,
                      fontSize: FONT_SIZE,
                      fontWeight: 500,
                    }}
                  >
                    Action
                  </Table.ColumnHeader>
                )}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.map((alert, i) => (
                <Table.Row
                  key={i}
                  _hover={{ bg: colors.rowHover }}
                  style={{ borderBottom: `1px solid ${colors.border}` }}
                >
                  <Table.Cell
                    px={6}
                    py={4}
                    fontWeight="semibold"
                    style={{
                      color: showFullTable ? colors.id : colors.link,
                      fontSize: FONT_SIZE,
                    }}
                  >
                    {alert.id}
                  </Table.Cell>
                  <Table.Cell px={6} py={4}>
                    {showFullTable ? (
                      <Text
                        fontWeight="medium"
                        style={{
                          color: colors.textPrimary,
                          fontSize: FONT_SIZE,
                        }}
                      >
                        {alert.country}
                      </Text>
                    ) : (
                      <Box>
                        <Text
                          fontWeight="medium"
                          style={{
                            color: colors.textPrimary,
                            fontSize: FONT_SIZE,
                          }}
                        >
                          {alert.country}
                        </Text>
                        <Text
                          style={{
                            color: colors.textSecondary,
                            fontSize: FONT_SIZE,
                          }}
                        >
                          ({alert.company})
                        </Text>
                      </Box>
                    )}
                  </Table.Cell>
                  {showFullTable && (
                    <Table.Cell
                      px={6}
                      py={4}
                      style={{
                        color: colors.textSecondary,
                        fontSize: FONT_SIZE,
                      }}
                    >
                      {alert.company}
                    </Table.Cell>
                  )}
                  <Table.Cell
                    px={6}
                    py={4}
                    style={{ color: colors.textSecondary, fontSize: FONT_SIZE }}
                  >
                    {alert.category}
                  </Table.Cell>
                  <Table.Cell px={6} py={4}>
                    <SeverityBadge severity={alert.severity} />
                  </Table.Cell>
                  {showFullTable && (
                    <>
                      <Table.Cell
                        px={6}
                        py={4}
                        fontWeight="semibold"
                        style={{ color: "#DC2626", fontSize: FONT_SIZE }}
                      >
                        {alert.delta} days
                      </Table.Cell>
                      <Table.Cell
                        px={6}
                        py={4}
                        style={{
                          color: colors.textSecondary,
                          fontSize: FONT_SIZE,
                        }}
                      >
                        {formatDateTime(alert.created)}
                      </Table.Cell>
                    </>
                  )}
                  <Table.Cell px={6} py={4}>
                    <StatusBadge status={alert.status} />
                  </Table.Cell>
                  {showFullTable && (
                    <Table.Cell px={6} py={4}>
                      {(() => {
                        const createdTicket = ticketsByAlertId[alert.id];
                        const ticketNumber = createdTicket?.ticketNumber ?? alert.ticketNumber;
                        const ticketSysId = createdTicket?.ticketSysId ?? alert.ticketSysId;
                        if (ticketNumber && ticketSysId) {
                          return (
                            <Link
                              href={getTicketLink(ticketSysId)}
                              target="_blank"
                              rel="noreferrer noopener"
                              fontWeight="medium"
                              color={colors.ticket}
                              fontSize={FONT_SIZE}
                              textDecoration="underline"
                              _hover={{ color: colors.linkHover }}
                            >
                              {ticketNumber}
                            </Link>
                          );
                        }
                        return (
                          <Button
                            variant="ghost"
                            fontWeight="medium"
                            color={colors.ticket}
                            fontSize={FONT_SIZE}
                            backgroundColor={colors.ticketBg}
                            disabled={creatingTicketAlertId === alert.id}
                            loading={creatingTicketAlertId === alert.id}
                            _hover={{
                              backgroundColor: colors.ticketHover,
                            }}
                            _active={{ backgroundColor: colors.ticketHover }}
                            _focus={{ boxShadow: "none", outline: "none" }}
                            _focusVisible={{
                              boxShadow: "none",
                              outline: "none",
                            }}
                            onClick={async () => {
                              if (!alert.id) return;
                              const alertId = Number(alert.id);
                              if (!Number.isFinite(alertId)) return;
                              setCreatingTicketAlertId(alert.id);
                              try {
                                const response = await createTicket({
                                  alertId,
                                });
                                const created = response.data;
                                if (created?.ticketNumber && created?.ticketSysId) {
                                  setTicketsByAlertId((prev) => ({
                                    ...prev,
                                    [alert.id]: {
                                      ticketNumber: created.ticketNumber,
                                      ticketSysId: created.ticketSysId,
                                    },
                                  }));
                                }
                                onTicketCreated?.();
                              } finally {
                                setCreatingTicketAlertId(null);
                              }
                            }}
                          >
                            <CirclePlus size={14} />
                            Create Ticket
                          </Button>
                        );
                      })()}
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>

      <Dialog.Root open={isModalOpen} onOpenChange={(e) => setIsModalOpen(e.open)}>
        <Dialog.Backdrop />
        <Dialog.Positioner p={4}>
          <Dialog.Content
            maxW="7xl"
            style={{ backgroundColor: "#FFFFFF" }}
            h="75vh"
            overflow="auto"
            css={getScrollbarStyles()}
          >
            <Dialog.Header>
              <Dialog.Title
                style={{
                  color: colors.textPrimary,
                  fontSize: "1.25rem",
                  fontWeight: 600,
                }}
              >
                {selectedAlert ? "Alert Details" : "All Alerts"}
              </Dialog.Title>
              <Dialog.CloseTrigger>
                <CloseButton bg="transparent" />
              </Dialog.CloseTrigger>{" "}
            </Dialog.Header>
            <Dialog.Body>
              {selectedAlert ? (
                <Box>
                  <SimpleGrid columns={2} gap={4}>
                    <Box>
                      <Text fontSize="xs" style={{ color: colors.textSecondary }} mb={1}>
                        Alert ID
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" style={{ color: colors.textPrimary }}>
                        {selectedAlert.id}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" style={{ color: colors.textSecondary }} mb={1}>
                        Country
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" style={{ color: colors.textPrimary }}>
                        {selectedAlert.country}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" style={{ color: colors.textSecondary }} mb={1}>
                        Company
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" style={{ color: colors.textPrimary }}>
                        {selectedAlert.company}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" style={{ color: colors.textSecondary }} mb={1}>
                        Category
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" style={{ color: colors.textPrimary }}>
                        {selectedAlert.category}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" style={{ color: colors.textSecondary }} mb={1}>
                        Severity
                      </Text>
                      <SeverityBadge severity={selectedAlert.severity} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" style={{ color: colors.textSecondary }} mb={1}>
                        Status
                      </Text>
                      <StatusBadge status={selectedAlert.status} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" style={{ color: colors.textSecondary }} mb={1}>
                        Delta
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" style={{ color: "#DC2626" }}>
                        {selectedAlert.delta} days
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" style={{ color: colors.textSecondary }} mb={1}>
                        Created
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" style={{ color: colors.textPrimary }}>
                        {formatDateTime(selectedAlert.created)}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              ) : (
                <Box overflowX="auto">
                  <Table.Root>
                    <Table.Header>
                      <Table.Row style={{ backgroundColor: colors.headerBg }}>
                        <Table.ColumnHeader
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          px={4}
                          py={3}
                          style={{ color: colors.headerText }}
                        >
                          Alert ID
                        </Table.ColumnHeader>
                        <Table.ColumnHeader
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          px={4}
                          py={3}
                          style={{ color: colors.headerText }}
                        >
                          Country
                        </Table.ColumnHeader>
                        <Table.ColumnHeader
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          px={4}
                          py={3}
                          style={{ color: colors.headerText }}
                        >
                          Company
                        </Table.ColumnHeader>
                        <Table.ColumnHeader
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          px={4}
                          py={3}
                          style={{ color: colors.headerText }}
                        >
                          Category
                        </Table.ColumnHeader>
                        <Table.ColumnHeader
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          px={4}
                          py={3}
                          style={{ color: colors.headerText }}
                        >
                          Severity
                        </Table.ColumnHeader>
                        <Table.ColumnHeader
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          px={4}
                          py={3}
                          style={{ color: colors.headerText }}
                        >
                          Delta
                        </Table.ColumnHeader>
                        <Table.ColumnHeader
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          px={4}
                          py={3}
                          style={{ color: colors.headerText }}
                        >
                          Created
                        </Table.ColumnHeader>
                        <Table.ColumnHeader
                          fontSize="xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          px={4}
                          py={3}
                          style={{ color: colors.headerText }}
                        >
                          Status
                        </Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {data.map((alert, i) => (
                        <Table.Row key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <Table.Cell
                            px={4}
                            py={3}
                            fontSize="sm"
                            fontWeight="semibold"
                            style={{ color: colors.link }}
                          >
                            {alert.id}
                          </Table.Cell>
                          <Table.Cell
                            px={4}
                            py={3}
                            fontSize="sm"
                            fontWeight="medium"
                            style={{ color: colors.textPrimary }}
                          >
                            {alert.country}
                          </Table.Cell>
                          <Table.Cell
                            px={4}
                            py={3}
                            fontSize="sm"
                            style={{ color: colors.textSecondary }}
                          >
                            {alert.company}
                          </Table.Cell>
                          <Table.Cell
                            px={4}
                            py={3}
                            fontSize="sm"
                            style={{ color: colors.textSecondary }}
                          >
                            {alert.category}
                          </Table.Cell>
                          <Table.Cell px={4} py={3}>
                            <SeverityBadge severity={alert.severity} />
                          </Table.Cell>
                          <Table.Cell
                            px={4}
                            py={3}
                            fontSize="sm"
                            fontWeight="semibold"
                            style={{ color: "#DC2626" }}
                          >
                            {alert.delta} days
                          </Table.Cell>
                          <Table.Cell
                            px={4}
                            py={3}
                            fontSize="sm"
                            style={{ color: colors.textSecondary }}
                          >
                            {formatDateTime(alert.created)}
                          </Table.Cell>
                          <Table.Cell px={4} py={3}>
                            <StatusBadge status={alert.status} />
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </>
  );

  if (embedded) return content;
  return (
    <Box bg="white" borderRadius="xl" borderWidth="1px" boxShadow="sm" overflow="hidden">
      {content}
    </Box>
  );
};
